import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  Fun,
  IOWidget,
  any,
  fromJSX,
  nothing,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import { collectionEditorWidget } from '../collections/collectionEditor/collectionEditorWidget'
import { CollectionTeasersLayout } from '../collections/collectionTeasers/collectionTeasersLayout'
import {
  CollectionsData,
  CollectionsLoaderState,
} from '../collections/collectionsLoader/collectionsLoaderState'
import { PageHeaderLayout } from '../pageHeader/pageHeaderLayout'
import { PaginationLayout } from '../pagination/paginationLayout'
import { paywallWidget } from '../paywall/paywallWidget'
import { DashboardState, dashboardStateUpdaters } from './dashboardState'

export const collectionsPerPage = 12

export const dashboardWidget =
  (
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsState: CollectionsLoaderState,
    page: number
  ): IOWidget<
    DashboardState,
    Fun<DashboardState, DashboardState & { reloadCollections?: boolean }>
  > =>
  (dashboardState: DashboardState) => {
    const collections =
      collectionsState.kind == 'loading'
        ? collectionsState.value == undefined
          ? 'loading'
          : collectionsState.value
        : collectionsState.kind == 'loaded'
        ? collectionsState.value
        : 'error'

    const sortedCollections =
      collections == 'loading' || collections == 'error'
        ? collections
        : collections
            .map((collection) => collection)
            .sort((fst, snd) => fst.Name.localeCompare(snd.Name))

    const from = collectionsPerPage * (page - 1)
    const to = collectionsPerPage * page
    const sortedCollectionsPage =
      sortedCollections == 'loading' || sortedCollections == 'error'
        ? sortedCollections
        : sortedCollections.slice(from, to)

    return paywallWidget(
      isLoggedIn,
      userSubscriptionsState,
      undefined,
      false
    )(
      dashboardBaseWidget(
        userSubscriptionsState,
        sortedCollectionsPage,
        true
      )(dashboardState),
      any([
        dashboardBaseWidget(
          userSubscriptionsState,
          sortedCollectionsPage,
          false
        )(dashboardState),
        sortedCollections === 'error' || sortedCollections == 'loading'
          ? nothing()
          : fromJSX(() => (
              <PaginationLayout
                totalItems={sortedCollections.length}
                currentPage={page - 1}
                toPage={(newPage) =>
                  i18next.language == 'en'
                    ? `/en/dashboard?page=${newPage + 1}`
                    : `/nl/dashboard?page=${newPage + 1}`
                }
                itemsPerPage={collectionsPerPage}
              />
            )),
        collectionEditorWidget(
          false,
          undefined
        )(dashboardState.collectionCreatorState).map(
          dashboardStateUpdaters.collectionCreatorStateUpdater
        ),
      ])
    )
  }

const dashboardBaseWidget =
  (
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collections: CollectionsData | 'loading' | 'error',
    paywalled: boolean
  ): IOWidget<
    DashboardState,
    Fun<DashboardState, DashboardState & { reloadCollections?: boolean }>
  > =>
  (dashboardState) =>
    fromJSX((setState) => (
      <>
        <PageHeaderLayout
          kind="dashboard"
          title={
            userSubscriptionsState.kind === 'loaded'
              ? userSubscriptionsState.value.User.Name
              : ''
          }
          expandable={{
            isExpanded: dashboardState.accountExpanded,
            onExpand: () =>
              setState(dashboardStateUpdaters.toggleAccountExpandable),
          }}
        />
        <CollectionTeasersLayout
          kind="dashboard"
          title={i18next.t('collection:yourCollections')}
          intro={
            collections == 'loading' ||
            collections == 'error' ||
            collections.length < 1
              ? i18next.t('collection:collectionsIntroEmpty')
              : i18next.t('collection:collectionsIntro')
          }
          collections={collections}
          paywalled={paywalled}
          onCreate={() => setState(dashboardStateUpdaters.startCreatingUpdater)}
        />
      </>
    ))
